import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { cn } from "../../utils/cn";

export interface FieldRowProps {
    /** The field label, rendered left in a fixed-width column. */
    label: React.ReactNode;
    /**
     * Optional trailing hint beside the label — typically a tooltip trigger.
     * Kept as a slot so this generic row never imports a domain control.
     */
    hint?: React.ReactNode;
    /**
     * The control, rendered right in the value cell. Required in edit mode
     * (the default). Ignored when `readOnly` is set — pass `value` instead.
     */
    children?: React.ReactNode;
    /**
     * Read-only mode. When `true`, the row renders a static label + `value`
     * (a plain settled value) instead of the editable control slot — same
     * field, flipped by mode. The container becomes a non-interactive
     * `<div>` (no wrapping `<label>`), the label column widens to `w-44`,
     * and the value sits in `text-foreground-base`.
     */
    readOnly?: boolean;
    /**
     * The settled value node, rendered right in read-only mode. Only read
     * when `readOnly` is set; ignored in edit mode (use `children`).
     */
    value?: React.ReactNode;
    /**
     * Value-cell chrome. Defaults to `w-48 shrink-0` — a fixed 192px column
     * for boxed inputs / selects. Pass `flex-1 min-w-0` (or similar) when the
     * control should fill the remaining row width (e.g. a segmented control).
     * Edit mode only — read-only mode owns its own value typography.
     */
    controlClassName?: string;
    /** Extend / override the row chrome. */
    className?: string;
}

/**
 * FieldRow — the standard "label-left, value-right" field row, mode-aware.
 *
 * In **edit mode** (default) it is the "label-left, control-right" form row:
 * a fixed-width label column (`w-40`) with an optional trailing hint slot, a
 * `gap-12` gutter, and a value cell that holds the control slot (`children`).
 *
 * In **read-only mode** (`readOnly`) it renders the same field as a settled
 * label + `value` — a wider `w-44` label column, `gap-8` gutter, `py-1` row
 * padding, value in `text-foreground-base`. The mode flips the layout /
 * typography; it is the same row.
 *
 * Generic Component: it knows no Solomon/YaaS entity and owns no control
 * type — the control (edit) or value (read-only) is passed as a slot, so this
 * row never imports a domain field. It is a pure label + slot; there is no
 * `type` switch. Composes the existing `Text` primitive for the label.
 *
 * @version 1.1.0
 * @published 2026-07-04
 * @history
 *   1.0.0 — Initial publish. Promoted from the client Programs
 *           ProgramDetail page-local `FieldRow` as a domain-free GC,
 *           adding `controlClassName` so a full-width control (segmented
 *           select) can opt out of the fixed value-cell width.
 *   1.1.0 — Made mode-aware. Added `readOnly` + `value`: the same row now
 *           renders a settled label+value (read-only) or the editable
 *           control (edit). Absorbs the client Programs
 *           `ProgramConfigurationRow` read-only summary row (pixel-for-pixel:
 *           `py-1` · `gap-8` · `w-44` label · `text-foreground-base` value).
 *           Back-compat: edit mode (no `readOnly`) is unchanged.
 *
 * @see FieldRow.intent.md   policy + recipes
 * @see FieldRow.layout.html intended layout
 */
export function FieldRow({
    label,
    hint,
    children,
    readOnly = false,
    value,
    controlClassName = "w-48 shrink-0",
    className
}: FieldRowProps): React.ReactElement {
    if (readOnly) {
        return (
            <div className={cn("py-1 flex items-start gap-8", className)}>
                <span className="flex items-center gap-1.5 w-44 shrink-0">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        {label}
                    </Text>
                    {hint}
                </span>
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-base"
                >
                    {value}
                </Text>
            </div>
        );
    }

    return (
        <label className={cn("flex items-center gap-12", className)}>
            <span className="flex items-center gap-1.5 w-40 shrink-0">
                <Text variant="small" className="text-foreground-muted">
                    {label}
                </Text>
                {hint}
            </span>
            <span className={controlClassName}>{children}</span>
        </label>
    );
}
